import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import Info from '../../../../../../components/channel-configuration/info';
import { InputSelect } from '../../../../../../components/input/select';
import { useBackdropContext } from '../../../../../../context/backdrop';
import {
  ChannelTypeInternal,
  GenericInputBistableModeInternal,
  GenericInputMonostableModeInternal,
  GenericInputTypeInternal,
  VersionedFeatureType,
} from '../../../../../../data-access/gql-types/graphql';
import { toastInfo, toastSuccess } from '../../../../../../utils/toast';
import { useSetInputTypesBatch } from '../../../../api/generic-input/generic-input.hooks';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { useDevicesAndChannels } from '../../../../hooks/devices-and-channels';
import { useCheckDeviceSupport } from '../../../../hooks/version-map/use-check-device-support';
import { ChannelType } from '../../../../types';
import { ChannelGenericInputType } from '../../../../types/channel/generic-input';
import {
  GenericInputBistableModeInternalRequest,
  GenericInputMonostableModeInternalRequest,
  GenericInputTypeInternalRequest,
} from '../../../../types/graphql-rest-converter';
import { InfoPageType } from '../../types';

type InputsTypeProps = {
  setInfoPage: Dispatch<SetStateAction<InfoPageType>>;
};

export const InputsType: React.FC<InputsTypeProps> = ({ setInfoPage }) => {
  const { t } = useTranslation('configuration');
  const { t: td } = useTranslation('device-settings');
  const [genericInputs, setGenericInputs] = useState<ChannelType[]>([]);
  const [genericInputType, setGenericInputType] = useState<GenericInputTypeInternal>(GenericInputTypeInternal.Unknown);
  const { channelId } = useParams<{ channelType: ChannelTypeInternal; channelId: string }>();
  const { channelList, setChannelList } = useDevicesAndChannels({ channelId });
  const { channel } = useDevicesAndChannelsContext();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { mutate } = useSetInputTypesBatch();
  const { checkSupport } = useCheckDeviceSupport();

  useEffect(() => {
    if (channel?.deviceId) {
      const inputs = channelList.filter(
        (x) => x.deviceId === channel.deviceId && x.data.type === ChannelTypeInternal.GenericInput,
      );

      setGenericInputs(inputs);

      if (inputs.length) {
        const allMonostable = inputs.every(
          (x) => (x.data as ChannelGenericInputType).genericInputType === GenericInputTypeInternal.Monostable,
        );

        if (allMonostable) {
          setGenericInputType(GenericInputTypeInternal.Monostable);
          return;
        }

        const allBistable = inputs.every(
          (x) => (x.data as ChannelGenericInputType).genericInputType === GenericInputTypeInternal.Bistable,
        );

        if (allBistable) setGenericInputType(GenericInputTypeInternal.Bistable);
      }
    }
  }, [channel?.deviceId, channelList]);

  const handleUpdateInputType = (value: GenericInputTypeInternal) => {
    const supported = checkSupport({
      feature: VersionedFeatureType.DeviceBindingsV2,
      supportKey: 'Core',
      currentVersion: channel?.currentFirmwareVersion,
    });

    if (!supported) toastInfo({ content: td('updateToUseFeature') });
    else {
      if (channel?.deviceId) {
        turnOnBackdrop();

        mutate(
          {
            deviceId: channel.deviceId,
            items: genericInputs.map((x) => ({
              channelId: x.id,
              genericInputType: GenericInputTypeInternalRequest[value],
              ...(value === GenericInputTypeInternal.Monostable
                ? {
                    monostableMode:
                      GenericInputMonostableModeInternalRequest[
                        (x.data as ChannelGenericInputType).monostableMode || GenericInputMonostableModeInternal.Normal
                      ],
                  }
                : {}),
              ...(value === GenericInputTypeInternal.Bistable
                ? {
                    bistableMode:
                      GenericInputBistableModeInternalRequest[
                        (x.data as ChannelGenericInputType).bistableMode || GenericInputBistableModeInternal.Normal
                      ],
                  }
                : {}),
            })),
          },
          {
            onSuccess: () => {
              toastSuccess({ content: t('editInputTypesBatchSuccess') });

              setChannelList((prev) => {
                const tempList = cloneDeep(prev);
                genericInputs.forEach((input) => {
                  const index = tempList.findIndex((x) => x.id === input.id);

                  if (index !== -1) {
                    (tempList[index].data as ChannelGenericInputType).genericInputType = value;
                  }
                });

                return tempList;
              });

              turnOffBackdrop();
            },
            onError: () => {
              turnOffBackdrop();
            },
          },
        );
      }
    }
  };

  return (
    <>
      {genericInputs.length ? (
        <>
          <hr />
          <Info title={t('inputType')} onClick={() => setInfoPage(InfoPageType.INPUT_TYPE)} />
          <InputSelect
            label={t('inputType')}
            value={genericInputType}
            onChange={handleUpdateInputType}
            options={[
              { label: t('monoStable'), value: GenericInputTypeInternal.Monostable },
              { label: t('biStable'), value: GenericInputTypeInternal.Bistable },
            ]}
          />
        </>
      ) : null}
    </>
  );
};
